import type { SedrRequirement } from '../../api/sedr';
import { sedrApi } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import {
  exportSedrRequirementsCsv,
  openSedrComplianceReport,
  SEDR_EXPORT_COLUMNS,
  type SedrExportColumn,
} from './sedrExport';
import { sedrErrorMessageKey } from './sedrErrors';

export type SedrExportFormat = 'csv' | 'pdf' | 'pdfDetailed' | 'report';
export type SedrExportScope = 'filtered' | 'all';

export function showSedrExportDialog(options: {
  roomId?: string;
  roomTitle: string;
  clientOrganization: string;
  filteredRequirements: SedrRequirement[];
  allRequirements: SedrRequirement[];
  allowServerPdf?: boolean;
  /** When true, obsolete reqs are currently hidden in the grid (default for PDF includeObsolete = false). */
  hideObsolete?: boolean;
}): void {
  const shell = createModalShell({
    titleKey: 'sedr.export.title',
    titleId: 'sedr-export-title',
    helpTopic: dialogHelpTopic('sedrExport'),
    className: 'sedr-export-dialog',
  });

  const allowPdf = Boolean(options.allowServerPdf && options.roomId);
  const includeObsoleteDefault = options.hideObsolete === false;
  const columnChecks = SEDR_EXPORT_COLUMNS.map((column) => `
    <label class="choice-row sedr-export-dialog__column">
      <input type="checkbox" name="column" value="${column}" checked />
      <span data-i18n="sedr.export.col.${column}"></span>
    </label>
  `).join('');

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-export-form">
      <fieldset class="sedr-export-dialog__fieldset">
        <legend data-i18n="sedr.export.format"></legend>
        ${allowPdf ? `
        <label class="choice-row sedr-export-dialog__option">
          <input type="radio" name="format" value="pdf" checked />
          <span data-i18n="sedr.export.formatPdf"></span>
        </label>
        <label class="choice-row sedr-export-dialog__option">
          <input type="radio" name="format" value="pdfDetailed" />
          <span data-i18n="sedr.export.formatPdfDetailed"></span>
        </label>
        ` : ''}
        <label class="choice-row sedr-export-dialog__option">
          <input type="radio" name="format" value="csv" ${allowPdf ? '' : 'checked'} />
          <span data-i18n="sedr.export.formatCsv"></span>
        </label>
        <label class="choice-row sedr-export-dialog__option">
          <input type="radio" name="format" value="report" />
          <span data-i18n="sedr.export.formatReport"></span>
        </label>
      </fieldset>
      <fieldset class="sedr-export-dialog__fieldset" id="sedr-export-scope-fieldset">
        <legend data-i18n="sedr.export.scope"></legend>
        <label class="choice-row sedr-export-dialog__option">
          <input type="radio" name="scope" value="filtered" checked />
          <span data-i18n="sedr.export.scopeFiltered"></span>
        </label>
        <label class="choice-row sedr-export-dialog__option">
          <input type="radio" name="scope" value="all" />
          <span data-i18n="sedr.export.scopeAll"></span>
        </label>
      </fieldset>
      <fieldset class="sedr-export-dialog__fieldset" id="sedr-export-columns-fieldset">
        <legend data-i18n="sedr.export.columns"></legend>
        <div class="sedr-export-dialog__columns">${columnChecks}</div>
      </fieldset>
      <label class="choice-row" id="sedr-export-obsolete-row" ${allowPdf ? '' : 'hidden'}>
        <input type="checkbox" name="includeObsolete" id="sedr-export-include-obsolete" ${includeObsoleteDefault ? 'checked' : ''} />
        <span data-i18n="sedr.export.includeObsolete"></span>
      </label>
      <p class="muted" id="sedr-export-pdf-hint" ${allowPdf ? '' : 'hidden'} data-i18n="sedr.export.pdfHint"></p>
      <p class="error-text" id="sedr-export-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.id = 'sedr-export-cancel';
  cancelButton.setAttribute('data-i18n', 'common.cancel');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'sedr-export-form');
  submitButton.setAttribute('data-i18n', 'sedr.export.confirm');

  shell.actions.append(cancelButton, submitButton);

  const errorEl = shell.body.querySelector('#sedr-export-error') as HTMLParagraphElement;
  const form = shell.body.querySelector('#sedr-export-form') as HTMLFormElement;
  const scopeFieldset = shell.body.querySelector('#sedr-export-scope-fieldset') as HTMLElement;
  const columnsFieldset = shell.body.querySelector('#sedr-export-columns-fieldset') as HTMLElement;
  const pdfHint = shell.body.querySelector('#sedr-export-pdf-hint') as HTMLElement;
  const obsoleteRow = shell.body.querySelector('#sedr-export-obsolete-row') as HTMLElement;

  const syncFormatUi = () => {
    const format = String(new FormData(form).get('format') ?? 'csv');
    const isPdf = format === 'pdf' || format === 'pdfDetailed';
    scopeFieldset.hidden = isPdf;
    columnsFieldset.hidden = isPdf;
    pdfHint.hidden = !isPdf;
    obsoleteRow.hidden = !isPdf;
  };
  syncFormatUi();
  form.addEventListener('change', syncFormatUi);

  const refreshLocale = () => applyI18n(shell.dialog);
  refreshLocale();
  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);
  document.body.append(shell.backdrop);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      const data = new FormData(form);
      const format = String(data.get('format') ?? 'csv') as SedrExportFormat;
      const scope = String(data.get('scope') ?? 'filtered') as SedrExportScope;
      const columns = data.getAll('column').map(String) as SedrExportColumn[];
      const includeObsolete = data.get('includeObsolete') === 'on';

      if (format === 'pdf' || format === 'pdfDetailed') {
        if (!options.roomId) {
          errorEl.hidden = false;
          errorEl.textContent = t('status.error');
          return;
        }
        submitButton.disabled = true;
        try {
          if (format === 'pdfDetailed') {
            await sedrApi.downloadComplianceReportDetailedPdf(options.roomId, includeObsolete);
          } else {
            await sedrApi.downloadComplianceReportPdf(options.roomId, includeObsolete);
          }
          close();
        } catch (cause) {
          errorEl.hidden = false;
          errorEl.textContent = t(sedrErrorMessageKey(cause));
          submitButton.disabled = false;
        }
        return;
      }

      if (columns.length === 0) {
        errorEl.hidden = false;
        errorEl.textContent = t('sedr.export.columnsRequired');
        return;
      }

      const requirements = scope === 'all'
        ? options.allRequirements
        : options.filteredRequirements;

      if (format === 'report') {
        openSedrComplianceReport({
          title: options.roomTitle,
          clientOrganization: options.clientOrganization,
          requirements,
          columns,
        });
      } else {
        exportSedrRequirementsCsv(requirements, { columns });
      }

      close();
    })();
  });
}
